import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { HEADER_POPOVER_Z_INDEX } from '@/shared/lib/constants';
import { DEFAULT_SEARCH } from '@/shared/lib/route';
import { Button } from '@/shared/ui/button/Button';
import { Popover } from '@/shared/ui/Popover';
import { PageSizeSelector } from '@/shared/ui/table/components/PageSizeSelector';
import { Text } from '@/shared/ui/text';

type Props = {
  /**
   * 現在のページサイズ
   *
   * 指定するとページサイズセレクタが表示される。
   * ほとんどのテーブルで表示件数の切り替えが必要なため、デフォルトで組み込んでいる。
   * テーブル以外の用途（分析画面など）ではこのpropsを省略してページサイズセレクタを非表示にできる。
   */
  pageSize?: number;
  /** ページサイズ以外の追加設定（金額単位など） */
  children?: ReactNode;
  /** デフォルト値から変更されているかの追加判定（children の設定状態など） */
  hasCustomSettings?: boolean;
  /** contentAlign の方向（デフォルト: "start"） */
  contentAlign?: 'start' | 'center' | 'end';
};

/**
 * 表示設定Popover
 *
 * 表示設定の切り替えを提供する汎用コンポーネント。
 * children で追加の設定項目を差し込み可能。
 * pageSize を指定するとページサイズセレクタが表示される。
 */
export const DisplaySettingsPopover: FC<Props> = ({
  pageSize,
  children,
  hasCustomSettings = false,
  contentAlign = 'start',
}) => {
  const { t } = useTranslation();

  const showPageSize = pageSize !== undefined;
  const isPageSizeChanged = showPageSize && pageSize !== DEFAULT_SEARCH.pageSize;
  const isChanged = hasCustomSettings || isPageSizeChanged;

  return (
    <Popover
      contentAlign={contentAlign}
      contentClassName="w-[280px] p-4"
      contentStyle={{ zIndex: HEADER_POPOVER_Z_INDEX }}
      content={
        <div className="flex flex-col gap-6">
          <Text variant="subhead">{t('common.table.display-settings')}</Text>
          {children}
          {showPageSize && (
            <div className="flex flex-col gap-1">
              <Text variant="label" color="muted">
                {t('common.table.pagination.display-count-label')}
              </Text>
              <PageSizeSelector pageSize={pageSize} />
            </div>
          )}
        </div>
      }
    >
      <Button
        variant="outline"
        type="button"
        className="relative"
        aria-label={t('common.table.display-settings')}
      >
        <AdjustmentsHorizontalIcon className="h-4 w-4" />
        {isChanged && (
          <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-brand-600" />
        )}
      </Button>
    </Popover>
  );
};
